import { defineComponent, PropType } from 'vue';
import { product_search_product } from '../../objects/v1_3/product/search/product_search_product';
import { Booking } from '../../services/booking';
import { Product } from '../../services/product';
import { product_search } from '../../objects/v1_3/product/search/product_search';
import DualRangeSlider from './dual_range_slider.vue';

const Book = new Booking();
const Prod = new Product();

type SectionKey =
  | 'sortBy' | 'search' | 'departingFrom' | 'duration' | 'price'
  | 'dates'  | 'accommodation' | 'groupSize' | 'destinations' | 'highlights';

export default defineComponent({
  name: 'TourFilterPanel',
  components: { DualRangeSlider },

  props: {
    tours: {
      type: Array as PropType<product_search_product[]>,
      required: true,
      default: () => [],
    },
  },

  emits: ['filtered'],

  data() {
    return {
      drawerOpen: false as boolean,
      openRoomInfo: '' as string,
      sectionsOpen: {
        sortBy:        false,
        search:        false,
        departingFrom: false,
        duration:      false,
        price:         false,
        dates:         false,
        accommodation: false,
        groupSize:     false,
        destinations:  false,
        highlights:    false,
      } as Record<SectionKey, boolean>,

      // Filter values
      searchInput:    '' as string,
      searchTimer:    null as ReturnType<typeof setTimeout> | null,
      departingFrom:  '' as string,
      durationMin:    0  as number,
      durationMax:    0  as number,
      priceMin:       0  as number,
      priceMax:       0  as number,
      dateFrom:       '' as string,
      dateTo:         '' as string,
      dateTimer:      null as ReturnType<typeof setTimeout> | null,
      dateLoading:    false as boolean,
      accommodation:  [] as string[],
      groupSize:      '' as string,
      destinations:   [] as string[],
      highlights:     [] as string[],
      sortBy: '' as string,

      // Derived bounds
      durationBounds: { min: 0, max: 0 } as { min: number; max: number },
      priceBounds:    { min: 0, max: 0 } as { min: number; max: number },

      // Date availability result
      availableTourIds: null as string[] | null,

      // Cached prices keyed by tour_id
      priceCache: {} as Record<string, number>,
    };
  },

  computed: {
    sortSummary(): string | null {
        const labels: Record<string, string> = {
            'price_desc': 'Price: High to Low',
            'price_asc':  'Price: Low to High',
            'duration_asc': 'Shortest First',
            'duration_desc': 'Longest First',
        };
        return this.sortBy ? labels[this.sortBy] : null;
    },
    accommodationOptions(): string[] {
        const seen = new Set<string>();
        this.tours.forEach(t => {
            t.prices?.forEach(p => {
                p.amounts?.forEach((a: any) => {
                    if (a.room_type) seen.add(a.room_type);
                });
            });
        });
        return Array.from(seen).sort();
    },
    departureCities(): string[] {
      const seen = new Set<string>();
      this.tours.forEach(t => { if (t.start_location?.city) seen.add(t.start_location.city); });
      return Array.from(seen).sort();
    },

    // Dynamic options — empty until API adds the fields
    destinationOptions(): string[] {
      const seen = new Set<string>();
      this.tours.forEach(t => {
        ((t as any).destinations as string[] | undefined)?.forEach(d => seen.add(d));
      });
      return Array.from(seen).sort();
    },
    highlightOptions(): string[] {
      const seen = new Set<string>();
      this.tours.forEach(t => {
        ((t as any).highlights as string[] | undefined)?.forEach(h => seen.add(h));
      });
      return Array.from(seen).sort();
    },

    hasActiveFilters(): boolean {
      return (
        this.searchInput.trim()       !== ''                   ||
        this.departingFrom            !== ''                   ||
        this.durationMin              !== this.durationBounds.min ||
        this.durationMax              !== this.durationBounds.max ||
        this.priceMin                 !== this.priceBounds.min ||
        this.priceMax                 !== this.priceBounds.max ||
        this.dateFrom                 !== ''                   ||
        this.dateTo                   !== ''                   ||
        this.accommodation.length     > 0                      ||
        this.groupSize                !== ''                   ||
        this.destinations.length      > 0                      ||
        this.highlights.length        > 0
      );
    },

    // Section summaries (null = no active value = no chip shown)
    durationSummary(): string | null {
      if (this.durationMin === this.durationBounds.min && this.durationMax === this.durationBounds.max) return null;
      if (this.durationMin === 0) return `Up to ${this.durationMax} days`;
      return `${this.durationMin}–${this.durationMax} days`;
    },
    priceSummary(): string | null {
      if (this.priceMin === this.priceBounds.min && this.priceMax === this.priceBounds.max) return null;
      const sym = this.currencySymbol;
      return `${sym}${this.priceMin} – ${sym}${this.priceMax}`;
    },
    dateSummary(): string | null {
      if (!this.dateFrom && !this.dateTo) return null;
      const from = this.dateFrom ? this.formatDate(this.dateFrom) : null;
      const to   = this.dateTo   ? this.formatDate(this.dateTo)   : null;
      if (from && to) return `${from} → ${to}`;
      return from ? `From ${from}` : `To ${to}`;
    },
    accommodationSummary(): string | null {
        if (!this.accommodation.length) return null;
        return this.accommodation.map(a => a === '' ? 'Day Tours' : a).join(', ');
    },
    groupSizeSummary(): string | null {
      if (!this.groupSize) return null;
      return `Max ${this.groupSize}`;
    },

    currencySymbol(): string {
        const mask = this.$store.getters.get_Currency?.currency_info?.currency_mask ?? '';
        return mask.charAt(0);
    },

    // Date input bounds
    dateFromMin(): string { return this.toDateString(new Date()); },
    dateFromMax(): string {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 2);
      return this.toDateString(d);
    },
    dateToMin(): string {
      const base = this.dateFrom ? new Date(this.dateFrom) : new Date();
      base.setDate(base.getDate() + 1);
      return this.toDateString(base);
    },
  },

  methods: {
    // ── Bounds initialisation ──────────────────────────────────────────────

    initialiseBounds() {
      this.buildPriceCache();
      this.setDurationBounds();
      this.setPriceBounds();
      console.log('currency', this.$store.getters.get_Currency);
    },

    buildPriceCache() {
      const cache: Record<string, number> = {};
      this.tours.forEach(t => {
        cache[t.tour_id] = Prod.product_pricing(
          Book.Pricing_Age_Category(this.$store),
          this.$store.getters.get_Currency,
          t
        );
      });
      this.priceCache = cache;
    },

    toggleRoomInfo(opt: string) {
        this.openRoomInfo = this.openRoomInfo === opt ? '' : opt;
    },

    getRoomDescription(room: string): string {
        if (room === '') return 'Tours that return on the same day with no overnight stay.';
        const r = room.toLowerCase();
        if (r.includes('private quad'))   return 'Private Quad Room: Private hostel room, suitable for 4 people.';
        if (r.includes('private triple')) return 'Private Triple Room: Private hostel room, suitable for 3 people.';
        if (r.includes('private twin'))   return 'Private Twin Room: Private hostel room, suitable for 2 people.';
        if (r.includes('twin'))           return 'Twin Room: 1 room with 2 single beds. Suitable for 2 people, one bed each.';
        if (r.includes('double'))         return 'Double Room: 1 room with 1 double bed. Suitable for 2 people to share.';
        if (r.includes('single'))         return 'Single Room: 1 room with 1 single bed. Suitable for 1 person. Single occupancy supplement applies.';
        if (r.includes('family 3'))       return 'Family 3 Room: 1 room with 1 double bed and 1 single bed. Suitable for 3 people.';
        if (r.includes('family 4'))       return 'Family 4 Room: 1 room with 1 double bed and 2 single beds. Suitable for 4 people.';
        if (r.includes('dorm'))           return 'Dorm Bed: 1 mixed-gender hostel room shared with multiple travellers, one bed each.';
        return '';
    },

    setDurationBounds() {
        if (!this.tours.length) return;
        const days = this.tours.map(t => this.getTourDurationInDays(t));
        const min = Math.min(...days);
        const max = Math.max(...days);
        this.durationBounds = { min, max };
        if (this.durationMin === 0 || this.durationMin < min) this.durationMin = min;
        if (this.durationMax === 0 || this.durationMax > max) this.durationMax = max;
    },

    setPriceBounds() {
      const prices = Object.values(this.priceCache).filter(p => p > 0);
      if (!prices.length) return;
      const min = Math.floor(Math.min(...prices));
      const max = Math.ceil(Math.max(...prices));
      this.priceBounds = { min, max };
      if (this.priceMin === 0 || this.priceMin < min) this.priceMin = min;
      if (this.priceMax === 0 || this.priceMax > max) this.priceMax = max;
  },

    getTourDurationInDays(tour: product_search_product): number {
        let days = tour.duration?.days ?? 0;
        if ((tour.duration?.hours ?? 0) >= 1) {
            days++;
        }
        return Math.max(days, 1);
    },

    // ── Search ────────────────────────────────────────────────────────────

    onSearchInput(e: Event) {
      this.searchInput = (e.target as HTMLInputElement).value;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applyFilters(), 300);
    },

    // ── Date availability ─────────────────────────────────────────────────

    onDateChange() {
      if (!this.dateFrom && !this.dateTo) {
        this.availableTourIds = null;
        this.applyFilters();
        return;
      }
      if (this.dateTimer) clearTimeout(this.dateTimer);
      this.dateTimer = setTimeout(() => this.fetchDateAvailability(), 400);
    },

    async fetchDateAvailability() {
        this.dateLoading = true;
        try {
            let from = this.dateFrom;
            let to   = this.dateTo;

            if (from && !to) {
                const d = new Date(from);
                d.setFullYear(d.getFullYear() + 1);
                to = this.toDateString(d);
            } else if (!from && to) {
                from = to;
            }

            const result = await Prod.Get_Product_Search_Date_Range([], from, to);
            this.availableTourIds = result.products.map(p => p.tour_id);
        } catch (err) {
            console.error('[TourFilterPanel] Date availability error:', err);
            this.availableTourIds = null;
        } finally {
            this.dateLoading = false;
            this.applyFilters();
        }
    },

    // ── Core filter logic ─────────────────────────────────────────────────

    applyFilters() {
      let result = [...this.tours];

      // Search (debounced, stop words stripped)
      const term = this.searchInput.trim().toLowerCase();
      if (term) {
        const stop  = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','by','from','is','it','its','this','that','are','was','were']);
        const terms = term.split(/\s+/).filter(w => !stop.has(w) && w.length > 1);
        if (terms.length) {
          result = result.filter(t => {
            const hay = `${t.name} ${t.description_headline} ${t.description_short} ${t.description}`.toLowerCase();
            return terms.every(w => hay.includes(w));
          });
        }
      }

      // Departing from
      if (this.departingFrom) {
        result = result.filter(t => t.start_location?.city === this.departingFrom);
      }

      // Duration
      if (this.durationMin !== this.durationBounds.min || this.durationMax !== this.durationBounds.max) {
        result = result.filter(t => {
            const d = this.getTourDurationInDays(t);
            return d >= this.durationMin && d <= this.durationMax;
        });
      }

      // Price
      if (this.priceMin !== this.priceBounds.min || this.priceMax !== this.priceBounds.max) {
        result = result.filter(t => {
          const p = this.priceCache[t.tour_id] ?? 0;
          return p >= this.priceMin && p <= this.priceMax;
        });
      }

      // Date availability
      if (this.availableTourIds !== null) {
        const ids = new Set(this.availableTourIds.map(id => id.toUpperCase()));
        result = result.filter(t => ids.has((t.tour_id ?? '').toUpperCase()));
      }

      // Accommodation — passes through if API field absent
        if (this.accommodation.length > 0) {
            result = result.filter(t => {
                const types = (t as any).accommodationTypes as string[] | undefined;

                if (types === undefined) {
                    const hasRoomTypes = t.prices?.some(p =>
                        p.amounts?.some((a: any) => a.room_type)
                    ) ?? false;
                    const isDayTour = !hasRoomTypes;

                    if (isDayTour) return this.accommodation.includes('');

                    const tourRoomTypes = new Set<string>();
                    t.prices?.forEach(p => {
                        p.amounts?.forEach((a: any) => {
                            if (a.room_type) tourRoomTypes.add(a.room_type);
                        });
                    });
                    return this.accommodation.some(a => tourRoomTypes.has(a));
                }

                return this.accommodation.some(a => types.includes(a));
            });
        }

      // Group size — passes through if API field absent
      if (this.groupSize) {
        result = result.filter(t => {
          const size = (t as any).groupSize as string | undefined;
          return size === undefined ? true : size === this.groupSize;
        });
      }

      // Destinations — passes through if API field absent
      if (this.destinations.length > 0) {
        result = result.filter(t => {
          const dests = (t as any).destinations as string[] | undefined;
          return dests === undefined ? true : this.destinations.some(d => dests.includes(d));
        });
      }

      // Highlights — passes through if API field absent
      if (this.highlights.length > 0) {
        result = result.filter(t => {
          const hl = (t as any).highlights as string[] | undefined;
          return hl === undefined ? true : this.highlights.some(h => hl.includes(h));
        });
      }

      if (this.sortBy) {
        result = [...result].sort((a, b) => {
            if (this.sortBy === 'price_asc')      return (this.priceCache[a.tour_id] ?? 0) - (this.priceCache[b.tour_id] ?? 0);
            if (this.sortBy === 'price_desc')     return (this.priceCache[b.tour_id] ?? 0) - (this.priceCache[a.tour_id] ?? 0);
            if (this.sortBy === 'duration_asc')   return this.getTourDurationInDays(a) - this.getTourDurationInDays(b);
            if (this.sortBy === 'duration_desc')  return this.getTourDurationInDays(b) - this.getTourDurationInDays(a);
            return 0;
        });
    }

      this.$emit('filtered', result);
    },

    // ── Clear ─────────────────────────────────────────────────────────────

    clearAll() {
      this.searchInput    = '';
      this.departingFrom  = '';
      this.durationMin    = this.durationBounds.min;
      this.durationMax    = this.durationBounds.max;
      this.priceMin       = this.priceBounds.min;
      this.priceMax       = this.priceBounds.max;
      this.dateFrom       = '';
      this.dateTo         = '';
      this.accommodation  = [];
      this.groupSize      = '';
      this.destinations   = [];
      this.highlights     = [];
      this.availableTourIds = null;
      this.applyFilters();
      this.sortBy = '';
    },

    clearSection(section: SectionKey) {
      ({
        search:        () => { this.searchInput = ''; },
        departingFrom: () => { this.departingFrom = ''; },
        duration:      () => { this.durationMin = this.durationBounds.min; this.durationMax = this.durationBounds.max; },
        price:         () => { this.priceMin = this.priceBounds.min; this.priceMax = this.priceBounds.max; },
        dates:         () => { this.dateFrom = ''; this.dateTo = ''; this.availableTourIds = null; },
        accommodation: () => { this.accommodation = []; },
        groupSize:     () => { this.groupSize = ''; },
        destinations:  () => { this.destinations = []; },
        highlights:    () => { this.highlights = []; },
        sortBy: () => { this.sortBy = ''; },
      } as Record<SectionKey, () => void>)[section]?.();
      this.applyFilters();
    },

    toggleSection(section: SectionKey) {
      this.sectionsOpen[section] = !this.sectionsOpen[section];
    },

    // ── Drawer ────────────────────────────────────────────────────────────

    openDrawer()  { this.drawerOpen = true;  document.body.style.overflow = 'hidden'; },
    closeDrawer() { this.drawerOpen = false; document.body.style.overflow = '';       },
    onEscape(e: KeyboardEvent) { if (e.key === 'Escape') this.closeDrawer(); },

    // ── Helpers ───────────────────────────────────────────────────────────

    toDateString(d: Date): string {
      return d.toISOString().split('T')[0];
    },
    formatDate(str: string): string {
      try {
        return new Date(str).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      } catch { return str; }
    },
  },

  watch: {
    tours: {
      handler() { 
            this.initialiseBounds(); 
        },
    },
    departingFrom()      { this.applyFilters(); },
    durationMin(val)     { this.applyFilters(); },
    durationMax()        { this.applyFilters(); },
    priceMin()           { this.applyFilters(); },
    priceMax()           { this.applyFilters(); },
    accommodation()      { this.applyFilters(); },
    groupSize()          { this.applyFilters(); },
    destinations()       { this.applyFilters(); },
    highlights()         { this.applyFilters(); },
    sortBy() { this.applyFilters(); },
    dateFrom(val) {
        if (val && this.dateTo && val > this.dateTo) {
            this.dateTo = '';
        }
        this.onDateChange();
    },
    dateTo() { 
        this.onDateChange(); 
    },
  },

    mounted() {
        this.initialiseBounds();
        window.addEventListener('keydown', this.onEscape);
    },
    unmounted() { 
        window.removeEventListener('keydown', this.onEscape); 
        document.body.style.overflow = ''; 
    },
});